/**
 * The unified run timeline — one chronological list interleaving a run's turns
 * (`useTurns`, `/events`) with its general log records (`useLogs`, `/logs`), so
 * the cost of a turn and the evidence around it are read together (issue #34,
 * spec §5 of the 2026-10-27 design).
 *
 * PRESENTATIONAL ONLY. Every ordering decision, the frontier and the withheld
 * arithmetic live in the pure `lib/run-timeline.ts`. If you find yourself
 * comparing two timestamps in this file, the comparison belongs there.
 *
 * ## The severity floor (spec §5.8)
 *
 * Same ladder and default as `LogsPanel`; it re-keys `useLogs`, which refetches
 * from page 1. Two consequences, stated here because both look like something
 * to optimize away:
 *
 * - **Each floor change costs a full-prefix S3 read** (#160, open). That is
 *   unchanged from `LogsPanel` and therefore not a regression — but it is why
 *   the loaded pages must **NOT** be cached across floor changes. The filtered
 *   set differs per floor, so the frontier does too, and reusing a higher
 *   floor's pages would put the frontier at a record that is not the last one
 *   in the current filter.
 * - **This view and `LogsPanel` share ONE `useLogs` cache**, because the query
 *   key is `['logs', producer, sessionId, severity]`. Switching between the two
 *   views at the same floor therefore costs nothing and a floor change
 *   refetches for both. Do not add a second hook.
 *
 * Turn rows are NEVER filtered by severity: a floor is a statement about log
 * records, and applying it to turns would silently move the cost column away
 * from the run header's total, which is the one number a reader cross-checks.
 *
 * ## The frontier (spec §5.2) — the two gates are DIFFERENT
 *
 * `frontier` is `null` **iff the log set is exhausted**, NOT iff nothing is
 * withheld. They come apart whenever more logs remain but every turn is already
 * at or before the last loaded one: `frontier` is a string and
 * `withheldTurnCount` is 0. So the withheld-turn notice is gated on the COUNT
 * and the `Load more` affordance on the FRONTIER. Gating both on the frontier
 * renders "0 later turns are below the loaded logs"; gating both on the count
 * hides the only way to fetch the rest.
 *
 * ## A failed plane is not the frontier case (spec §5.10)
 *
 * The frontier is lossless and one click from complete; a failed read has no
 * remedy at all, and per #160 it is likeliest on the long runs most worth
 * debugging. So the surviving half IS rendered — withholding the only data we
 * hold is not caution — but inside `.timeline-degraded`, which is the
 * mechanism. The heading is not: a banner over markup that still looks like the
 * merged timeline invites exactly the false correlation this view exists to
 * kill, because readers skim markers (§5.2's own rejected alternative).
 */

import { useState, type ReactNode } from 'react';
import { useLogs, useTurns, type LogLine, type TurnEvent } from '../lib/queries';
import { mergeTimeline, type TimelineRow } from '../lib/run-timeline';
import { fmtCost, fmtTokens, TURN_COST_UNREPORTED, turnCostIsUnreported } from '../lib/run-format';
import { SEVERITIES, severityClass } from './LogsPanel';

/** Why an epoch-dated row is where it is. Detection, not repair: Watchtower
 *  cannot recover a timestamp a producer never sent (spec §5.7). */
const UNKNOWN_TS_TITLE =
  'The producer sent no usable timestamp for this record, so it is filed at'
  + ' the Unix epoch. Its position here is not when it happened.';

function UnknownTimePill() {
  return (
    <span className="pill pill-warn timeline-unknown-ts" title={UNKNOWN_TS_TITLE}>
      unknown time
    </span>
  );
}

function TurnRow({ turn, unknownTime, runCostUsd }: {
  turn: TurnEvent;
  unknownTime: boolean;
  runCostUsd: number;
}) {
  // A missing per-turn cost renders as a placeholder, never as an authoritative
  // $0.0000 — the run above it is costed and a bare zero would read as a price.
  const unreported = turnCostIsUnreported(turn.cost_usd, runCostUsd, turn.unpriced);
  return (
    <>
      <span className="timeline-ts mono">{turn.ts_iso}</span>
      {unknownTime && <UnknownTimePill />}
      <span className="pill timeline-kind">turn</span>
      <span className="timeline-model">{turn.model}</span>
      <span className="timeline-tokens">
        {fmtTokens(turn.input_tokens)} / {fmtTokens(turn.output_tokens)}
      </span>
      <span className="timeline-cache">
        cache {fmtTokens(turn.cache_read_tokens)} / {fmtTokens(turn.cache_creation_tokens)}
      </span>
      <span className="timeline-cost mono">
        {unreported ? (
          <span className="turn-cost-unreported" title={TURN_COST_UNREPORTED}>
            —
          </span>
        ) : (
          fmtCost(turn.cost_usd)
        )}
      </span>
    </>
  );
}

/**
 * `LogsPanel`'s log-row markup, copied rather than re-invented.
 *
 * **FIVE CHILDREN ARE BYTE-IDENTICAL TO `LogsPanel`'s — NOT SIX, AND THE SIXTH
 * IS THE BODY.** Enumerated rather than claimed, since an identity claim false
 * in a named respect is worse than none — and this one has now been corrected
 * three times, each time about a different respect. The five: the `.log-ts mono`
 * timestamp, the `.log-sev` span dressed by the shared `severityClass`, the
 * conditional `.log-event`, the `.truncated-marker`, and the collapsed
 * `<details className="log-attrs">` holding `JSON.stringify(attrs, null, 2)`.
 * Attrs are never coerced and carry no redaction layer (spec §5.9), and a long
 * body is never truncated in JS on the belief content is absent.
 *
 * **The body `<code>` is MODIFIED, not shared** (difference 5): it keeps
 * `String(body)` and the `log-body` class, and adds an `id` plus a conditional
 * `log-body-expanded`. So the row is not "`LogsPanel`'s children plus two added
 * siblings" — one of the shared children changed, which is the framing the
 * earlier revisions of this comment got wrong. **That `id` is load-bearing and
 * is the thing a reader who trusts an identity claim deletes:** it is the target
 * of the toggle's `aria-controls`, so normalising the `<code>` back to
 * `LogsPanel`'s leaves the reveal working for a mouse and silently unannounced
 * to assistive tech. It is pinned by `the toggle's aria-controls resolves to the
 * body element` in the test file; without that test the edit is invisible.
 *
 * **It is NOT dressed identically, and difference 4 is why.** The clip is
 * scoped `.timeline-log .log-body`, so it reaches these rows and not
 * `LogsPanel`'s, which keep `word-break: break-word` and wrap in full. An
 * earlier revision of this paragraph claimed the CSS was shared; it never was,
 * and a false identical-bytes claim is worse than none, because it invites a
 * reader to "restore" a difference that is deliberate.
 *
 * **FIVE DELIBERATE DIFFERENCES — do not "restore" any of them:**
 *
 * 1. The `<li>`'s `key` is NOT `LogsPanel`'s `…#${seq}`. `seq` is the record's
 *    index within its ingest request, so two records from different requests in
 *    one session both carry `seq: 0`; keying on it is the live `LogsPanel` bug
 *    this view replaces. The key comes from `mergeTimeline` (`Rows` below).
 * 2. The `<li>` carries `timeline-row timeline-log` as well as `log-line`,
 *    because a row here sits in one list beside turn rows and the timeline's own
 *    layout has to reach it.
 * 3. A row whose timestamp is unusable carries the `unknown time` pill (spec
 *    §5.7). `LogsPanel` has no such marker, which is a gap there rather than a
 *    difference to reconcile away here.
 * 4. The body has an EXPAND-IN-PLACE control — issue #34's scope list names
 *    "expand-in-place log bodies" as a deliverable, and the CSS clip alone left
 *    a reader with a long body nothing to click. `LogsPanel` wraps its bodies
 *    in full and needs none.
 * 5. The body `<code>` carries an `id` and a conditional `log-body-expanded`
 *    class (see the paragraph above). `LogsPanel`'s has neither, and needs
 *    neither: it wraps its bodies in full, so there is nothing to reveal.
 *
 * **THE REVEAL IS NOT THE ATTRS DISCLOSURE, AND THE TWO MUST NEVER MERGE**
 * (spec §5.9): `attrs` carry identity attributes on every record from today's
 * producer and the body does not, so a reader opening one must not get the
 * other. Two controls, deliberately.
 *
 * **NOTHING HERE DECIDES IN JS WHETHER A BODY IS "LONG."** No length
 * threshold, no substring: the clip is CSS's job and the control is
 * unconditional. A threshold is how a "short" body holding one 4000-character
 * token would get no reveal — and it is also the JS truncation §5.9 forbids,
 * arriving as a presentation check.
 */
function LogRow({ line, unknownTime, rowKey, bodyExpanded, onToggleBody }: {
  line: LogLine;
  unknownTime: boolean;
  /** `mergeTimeline`'s own row key: the body's DOM id (so `aria-controls`
   *  points somewhere) AND the identity its expanded state is held under in the
   *  parent. Never a React key here. */
  rowKey: string;
  /** Held by `RunTimeline`, NOT by this row — see `expandedBodies` there. A
   *  severity change has to clear it, and a row cannot clear its own state on
   *  an event it never sees. */
  bodyExpanded: boolean;
  onToggleBody: (rowKey: string) => void;
}) {
  const bodyId = `timeline-body-${rowKey.replace(':', '-')}`;
  return (
    <>
      <span className="log-ts mono">{line.ts_iso}</span>
      {unknownTime && <UnknownTimePill />}
      <span className={`log-sev ${severityClass(line.severity)}`}>{line.severity}</span>
      {line.event_name && <span className="log-event">{line.event_name}</span>}
      <code
        id={bodyId}
        className={`log-body${bodyExpanded ? ' log-body-expanded' : ''}`}
      >
        {String(line.body)}
      </code>
      <button
        type="button"
        className="timeline-body-toggle"
        aria-expanded={bodyExpanded}
        aria-controls={bodyId}
        title="Show the whole body in place. The full value is always in the DOM; the row merely clips it."
        onClick={() => onToggleBody(rowKey)}
      >
        {bodyExpanded ? 'less' : 'more'}
      </button>
      {line.body_truncated && <span className="truncated-marker">…truncated</span>}
      {line.attrs && Object.keys(line.attrs).length > 0 && (
        <details className="log-attrs">
          <summary>attrs{line.attrs_truncated ? ' (truncated)' : ''}</summary>
          <pre>
            <code>{JSON.stringify(line.attrs, null, 2)}</code>
          </pre>
        </details>
      )}
    </>
  );
}

function Rows({ rows, runCostUsd, expandedBodies, onToggleBody }: {
  rows: TimelineRow[];
  runCostUsd: number;
  expandedBodies: ReadonlySet<string>;
  onToggleBody: (rowKey: string) => void;
}) {
  return (
    <ul className="timeline-rows">
      {rows.map((row) => (
        <li
          // `mergeTimeline`'s key, never `seq` (see `LogRow`'s difference 1).
          // Two CONSEQUENCES of it being positional for log rows, both
          // presentation state landing on a different record, both harmless and
          // both accepted — recorded here, beside the cause, so the next reader
          // does not rediscover them:
          //
          // - A partial-failure `Retry` does not clear `expandedBodies`. Unlike
          //   a floor change (which does clear it), a retry re-fetches the SAME
          //   filtered set, so the keys usually land back on the same records;
          //   when the set has moved, a row the reader never expanded renders
          //   pre-expanded. Clearing it on every refetch would instead discard
          //   an expansion the reader is looking at, which is the worse of the
          //   two.
          // - The attrs `<details>` can likewise come back stale-open, and only
          //   on a CACHED floor: with no `placeholderData` anywhere, an uncached
          //   floor change unmounts this `<ul>` entirely and the question does
          //   not arise.
          //
          // Neither is fixable by picking a better key: the response strips
          // `srcKey`/`srcLine` and `seq` restarts per ingest request, so there
          // is no globally unique field left to key on.
          key={row.key}
          className={
            row.kind === 'turn' ? 'timeline-row timeline-turn' : 'timeline-row timeline-log log-line'
          }
        >
          {row.kind === 'turn' ? (
            <TurnRow turn={row.turn} unknownTime={row.unknownTime} runCostUsd={runCostUsd} />
          ) : (
            <LogRow
              line={row.log}
              unknownTime={row.unknownTime}
              rowKey={row.key}
              bodyExpanded={expandedBodies.has(row.key)}
              onToggleBody={onToggleBody}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

export default function RunTimeline({
  producer,
  sessionId,
  runCostUsd,
}: {
  /** The run's address — never a claimed platform. */
  producer: string;
  sessionId: string;
  /** REQUIRED, never defaulted: `turnCostIsUnreported`'s pre-`unpriced`
   *  fallback compares against the run total, and a default of 0 would silently
   *  flip its answer for every turn of a costed run. */
  runCostUsd: number;
}) {
  const [severity, setSeverity] = useState('INFO');
  // **EXPANDED BODIES ARE HELD HERE, BY ROW KEY, SO THAT A FLOOR CHANGE CAN
  // CLEAR THEM — and the reason is the key.** A log row's key is its POSITION in
  // the loaded sequence (`log:0`, `log:1`, …), which spec §5.4 mandates by way of
  // §5.3: the server's own cursor is `[ts_iso, srcKey, srcLine]` precisely
  // because `seq` is not unique, and the response STRIPS `srcKey`/`srcLine`, so
  // the client holds no unique log id at all — position in the server-ordered
  // list is the only correct answer. (Read in §5.3, not taken from a brief.) So a
  // refetch at a new floor lands a DIFFERENT record under the same key, and
  // state left behind renders a row the reader never expanded as pre-expanded,
  // over somebody else's body. Held in the parent rather than per row because a
  // row cannot clear its own state on an event it never sees.
  //
  // The attrs `<details>` has the same hazard and is NOT reset here: its open
  // state is the browser's, on an uncontrolled element, and making it
  // controlled to reset it would be a larger change than the oddity costs.
  const [expandedBodies, setExpandedBodies] = useState<ReadonlySet<string>>(new Set());
  const toggleBody = (rowKey: string) =>
    setExpandedBodies((prev) => {
      const next = new Set(prev);
      if (!next.delete(rowKey)) next.add(rowKey);
      return next;
    });
  const turnsQuery = useTurns(producer, sessionId);
  const logsQuery = useLogs(producer, sessionId, severity);

  const turns = turnsQuery.data?.events ?? [];
  const logs = (logsQuery.data?.pages ?? []).flatMap((p) => p.logs);
  const merged = mergeTimeline(turns, logs, !logsQuery.hasNextPage);

  const control = (
    <div className="logs-controls">
      <label className="runs-control">
        <span className="runs-control-label">Severity ≥</span>
        <select
          value={severity}
          onChange={(e) => {
            setSeverity(e.target.value);
            // See `expandedBodies`: the same row keys will hold different
            // records once this refetches.
            setExpandedBodies(new Set());
          }}
        >
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  // Gated on `isLoading` — the INITIAL load — and never on `isFetching`, or a
  // `Load more` click would blank the list back to this note, which is the
  // false-flash §5.10 forbids from the other direction.
  const loading = turnsQuery.isLoading || logsQuery.isLoading;

  // **THE DEGRADED WRAPPER MEANS "THIS PLANE IS ABSENT", NOT "THIS PLANE
  // ERRORED", AND THE TWO COME APART EXACTLY WHEN A PREFIX LOADED FIRST.**
  // In TanStack Query v5 (probed against the installed 5.101.3) `isError` goes
  // true WITH `data` still present: a failed `fetchNextPage`, and a failed
  // `refetch` after a good first page, both answer
  // `{ isError: true, data: <the good pages> }`. Per #160 a page-2 timeout is
  // the LIKELIEST failure this component will ever meet, so this is the hot
  // path and not an edge.
  //
  // Branching on `isError` alone put the heading "Turns only — log records
  // could not be loaded" above real log rows, and — far worse — dropped the
  // withheld-turn notice while turns were being withheld, which §5.2 forbids
  // outright. A failed `Load more` does not invalidate the records already
  // loaded, and the frontier ALREADY states exactly how far the data is
  // complete; that is the whole point of it. So the predicate is three-way per
  // plane: ABSENT (error, no data) → the degraded region; PARTIAL (error, data
  // present) → the NORMAL timeline plus a non-blocking affordance saying that
  // MORE could not be loaded, never that the plane is missing; otherwise → as
  // normal. With data present, `Load more` and the withheld notice behave
  // exactly as on the happy path, because the loaded prefix is just as complete
  // as it was a moment ago.
  const turnsAbsent = turnsQuery.isError && turnsQuery.data === undefined;
  const logsAbsent = logsQuery.isError && logsQuery.data === undefined;
  const turnsPartial = turnsQuery.isError && !turnsAbsent;
  const logsPartial = logsQuery.isError && !logsAbsent;

  // One affordance per partially-failed plane. `.timeline-partial`, never
  // `.timeline-degraded`: nothing is missing from what is rendered, so dressing
  // it as a degraded region would make a complete prefix look incomplete.
  const partialNotices = (
    <>
      {/* `/events` returns the whole run in ONE response (it pages to completion
          server-side), so the turns plane cannot fail a `Load more`. The only
          way it errors while holding data is a failed REFETCH, which is what
          this says. */}
      {turnsPartial && (
        <p className="error timeline-partial timeline-partial-turns" role="alert">
          The turn records could not be refreshed — the turns shown are the ones already loaded{' '}
          <button type="button" className="btn" onClick={() => turnsQuery.refetch()}>
            Retry
          </button>
        </p>
      )}
      {logsPartial && (
        <p className="error timeline-partial timeline-partial-logs" role="alert">
          More log records could not be loaded — the timeline above is unchanged and still complete
          up to the last loaded record{' '}
          <button type="button" className="btn" onClick={() => logsQuery.refetch()}>
            Retry
          </button>
        </p>
      )}
    </>
  );

  // `Load more` is gated on the FRONTIER (the log set is not exhausted), never
  // on the withheld count — see the header. It is shared by the happy path and
  // the degraded region, which is paged exactly the same way.
  const loadMore = merged.frontier !== null && (
    <button type="button" className="btn" onClick={() => logsQuery.fetchNextPage()}>
      Load more
    </button>
  );

  let body: ReactNode;
  if (loading) {
    // One note and no rows: the state is brief and a partial render would flash
    // a timeline whose adjacencies are not yet true (spec §5.10).
    body = <p className="note">Loading timeline…</p>;
  } else if (turnsAbsent && logsAbsent) {
    // Both planes gone: there is no surviving half to wrap, so this is NOT
    // `.timeline-degraded` — that class means "part of a timeline", and there
    // is none.
    body = (
      <p className="error" role="alert">
        Failed to load this run&rsquo;s timeline{' '}
        <button
          type="button"
          className="btn"
          onClick={() => {
            turnsQuery.refetch();
            logsQuery.refetch();
          }}
        >
          Retry
        </button>
      </p>
    );
  } else if (turnsAbsent || logsAbsent) {
    const failed = turnsAbsent ? turnsQuery : logsQuery;
    const heading = turnsAbsent
      ? 'Logs only — turn records could not be loaded, so no correlation is shown here'
      : 'Turns only — log records could not be loaded, so no correlation is shown here';
    body = (
      <section className="timeline-degraded">
        <h4 className="timeline-degraded-heading">{heading}</h4>
        <button type="button" className="btn" onClick={() => failed.refetch()}>
          Retry
        </button>
        <Rows
          rows={merged.rows}
          runCostUsd={runCostUsd}
          expandedBodies={expandedBodies}
          onToggleBody={toggleBody}
        />
        {/* The surviving half is still PAGED when it is the log plane, and the
            `Load more` gate is the frontier unconditionally — a degraded region
            a reader cannot page is a second loss on top of the failed one. */}
        {loadMore}
        {/* The surviving plane can itself be PARTIAL — one plane absent and the
            other's page 2 failed is a real combination, and the two statements
            are different facts about different planes. */}
        {partialNotices}
      </section>
    );
  } else {
    body = (
      <>
        {/* Empty rows imply both planes are empty, which implies exhausted (the
            module treats an empty log set as exhausted whatever the flag says),
            so no second condition is needed. It lives INSIDE this branch rather
            than above it so that a PARTIAL failure over an empty prefix still
            renders its affordance instead of a bare "nothing recorded". */}
        {merged.rows.length === 0 ? (
          <p className="note">Nothing recorded for this run.</p>
        ) : (
          <Rows
            rows={merged.rows}
            runCostUsd={runCostUsd}
            expandedBodies={expandedBodies}
            onToggleBody={toggleBody}
          />
        )}
        {merged.withheldTurnCount > 0 && (
          <p className="note timeline-withheld">
            {merged.withheldTurnCount} later{' '}
            {merged.withheldTurnCount === 1 ? 'turn' : 'turns'} (
            {fmtCost(merged.withheldCostUsd)}){' '}
            {merged.withheldTurnCount === 1 ? 'is' : 'are'} below the loaded logs — Load more to
            extend the timeline
          </p>
        )}
        {loadMore}
        {partialNotices}
      </>
    );
  }

  return (
    <>
      <h3 className="section-title">Timeline</h3>
      {control}
      {body}
    </>
  );
}
